import React, { useState } from "react";
import { Copy, Check, UserPlus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

// JoinHereBox — the profile-as-referral CTA, in three flavors:
//
//   1. Anonymous viewer on someone else's profile
//      → Big "JOIN HERE" headline + the bare-bones address line
//        `GlobalEyes.TV/{owner}` + a Sign-up button that opens the
//        global auth modal. Before opening we drop the owner's handle
//        into `localStorage.ge.x_inv` so AuthContext.register can
//        attribute the signup to them (leaderboard credit).
//
//   2. Signed-in viewer on someone else's profile
//      → Render nothing. They're already "in"; we don't pester.
//
//   3. Owner viewing their own profile
//      → "This is your link · GlobalEyes.TV/{username} · [Copy]" so
//        the owner sees their clean, memorable URL and can grab it
//        in one tap. Principle #2: hammer the GlobalEyes.TV/handle
//        format as the brand surface.
//
// Mounted in two places:
//   • FeedPlayerOverlay left column (dynamic profile, between
//     comments and tip, profile-route only)
//   • ProfilePage static landing state (below the creator info bar)
//
// `variant`:
//   - "compact" (default) for the left-column overlay slot
//   - "hero"            for the static profile landing slot
//
// Compact = single-line address + button. Hero = bigger, centred,
// suitable for the larger landing canvas.
const JoinHereBox = ({ profileUsername, variant = "compact" }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const isOwn = user && user.username === profileUsername;
  const isAnonymous = !user;

  // Signed-in but viewing someone ELSE's profile — already "in";
  // hide rather than show a confusing "join again" CTA.
  if (!isAnonymous && !isOwn) return null;
  if (!profileUsername) return null;

  // The single source of truth for what we display + share. Always
  // bare. No path-less prefix, no www, no tracking junk in the
  // VISUAL — UTMs travel in the underlying click target (the X share
  // intent + the AuthContext referrer capture), not on screen.
  const cleanLink = `GlobalEyes.TV/${profileUsername}`;
  const fullUrl = `${window.location.origin}/${profileUsername}`;

  const handleJoinClick = () => {
    // Stamp the inviter so AuthContext.register forwards it as
    // `referrer_username` to the backend. The XAttributionBeacon
    // already does this when someone landed via `?inv=`, but a viewer
    // who navigated here organically (typed the URL, shared in DM,
    // etc.) wouldn't have it set — so we plant it now.
    try { localStorage.setItem("ge.x_inv", profileUsername); }
    catch { /* private mode — referral credit will just be skipped */ }
    // Open the global auth modal. The Header listens for this event
    // (same channel used by every "Sign up" CTA across the app).
    window.dispatchEvent(new CustomEvent("open-auth", { detail: { register: true } }));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      toast.success(`Copied — ${cleanLink}`);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy. Long-press the link to copy manually.");
    }
  };

  // ----- OWN PROFILE: "this is your link" pride card -----
  if (isOwn) {
    if (variant === "hero") {
      return (
        <div
          className="mx-auto max-w-md mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-center"
          data-testid="join-here-own-hero"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-emerald-400/80 font-semibold">
            Your link
          </p>
          <div className="mt-1.5 flex items-center justify-center gap-2">
            <span className="font-mono text-white text-sm sm:text-base">
              {cleanLink}
            </span>
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
              title="Copy your link"
              data-testid="join-here-copy-btn"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>
          <p className="text-[11px] text-gray-500 mt-1.5">
            Every signup that lands here counts toward your leaderboard score.
          </p>
        </div>
      );
    }
    return (
      <div
        className="px-3 py-2 text-center"
        data-testid="join-here-own-compact"
      >
        <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-400/80 font-semibold">
          Your link
        </p>
        <div className="mt-1 flex items-center justify-center gap-1.5">
          <span className="font-mono text-white text-xs">
            {cleanLink}
          </span>
          <button
            onClick={handleCopy}
            className="inline-flex items-center justify-center h-6 w-6 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition-colors"
            title="Copy"
            data-testid="join-here-copy-btn"
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          </button>
        </div>
      </div>
    );
  }

  // ----- ANONYMOUS VIEWER ON SOMEONE ELSE'S PROFILE: JOIN HERE -----
  if (variant === "hero") {
    return (
      <div
        className="mx-auto max-w-md mt-4 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-center"
        data-testid="join-here-anon-hero"
      >
        <p className="text-[11px] uppercase tracking-[0.18em] text-amber-300 font-semibold">
          Join here
        </p>
        <p className="mt-1.5 font-mono text-white text-sm sm:text-base">
          {cleanLink}
        </p>
        <button
          onClick={handleJoinClick}
          className="mt-2.5 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-400 hover:bg-amber-300 text-gray-900 text-xs font-semibold transition-colors"
          data-testid="join-here-signup-btn"
        >
          <UserPlus className="h-3.5 w-3.5" /> Sign up — 20 free credits
        </button>
        <p className="text-[11px] text-gray-500 mt-2">
          You&apos;ll keep this clean URL too: <span className="text-gray-300">GlobalEyes.TV/yourhandle</span>
        </p>
      </div>
    );
  }
  return (
    <div
      className="px-3 py-2.5 text-center"
      data-testid="join-here-anon-compact"
    >
      <p className="text-[10px] uppercase tracking-[0.16em] text-amber-300 font-semibold">
        Join here
      </p>
      <p className="mt-1 font-mono text-white text-xs">
        {cleanLink}
      </p>
      <button
        onClick={handleJoinClick}
        className="mt-1.5 inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-400 hover:bg-amber-300 text-gray-900 text-[11px] font-semibold transition-colors"
        data-testid="join-here-signup-btn"
      >
        <UserPlus className="h-3 w-3" /> Sign up
      </button>
    </div>
  );
};

export default JoinHereBox;
